import { useEffect, useMemo, useRef, useState } from 'react';
import {
  accountUrl,
  availabilityLabel,
  formatLedgerTime,
  formatNumber,
  verificationLabel,
} from '../lib/format.js';
import { useDialogA11y } from '../hooks/useDialogA11y.js';

function CopyButton({ value, label = 'Copy', copiedLabel = 'Copied', className = 'copy-button' }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);
  useEffect(() => () => clearTimeout(timerRef.current), []);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(typeof value === 'function' ? value() : value);
      setCopied(true);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };
  return <button className={className} onClick={copy}>{copied ? copiedLabel : label}</button>;
}

function controlsForProgram(rows) {
  const byIssuer = new Map();
  for (const row of rows) {
    if (!row.controls) continue;
    const key = row.kind === 'mpt' ? row.issuanceId : row.issuer;
    if (!byIssuer.has(key)) byIssuer.set(key, { key, kind: row.kind, ticker: row.ticker, ...row.controls });
  }
  return [...byIssuer.values()];
}

export function DetailDrawer({ program, supplies, flows = [], onClose }) {
  const drawerRef = useRef(null);
  useDialogA11y(Boolean(program), drawerRef, onClose);

  const recentFlows = useMemo(() => {
    if (!program) return [];
    return flows.filter((flow) => (
      flow.programId === program.id
      || flow.programId === program.catalog?.coveredBy
      || flow.matches?.some((match) => match.programId === program.id || match.programId === program.catalog?.coveredBy)
    )).slice(0, 6);
  }, [flows, program]);

  if (!program) return null;
  const rows = supplies.filter((row) => row.programId === program.id || row.programId === program.catalog?.coveredBy);
  const instruments = program.instruments || [];
  const controlSets = controlsForProgram(rows);
  const shareUrl = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('asset', program.id);
    return url.toString();
  };

  return (
    <div className="drawer-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <aside ref={drawerRef} className="detail-drawer" role="dialog" aria-modal="true" aria-labelledby="detail-title" tabIndex="-1">
        <div className="drawer-actions">
          <CopyButton value={shareUrl} label="Copy link" copiedLabel="Link copied" className="drawer-share" />
          <button className="drawer-close" onClick={onClose} aria-label="Close asset details">×</button>
        </div>
        <div className="detail-hero">
          <span className={`asset-monogram large category-${program.category}`}>{program.ticker.slice(0, 3)}</span>
          <span className="eyebrow">{program.category === 'rwa' ? 'Real-world asset' : program.category}</span>
          <h2 id="detail-title">{program.name}</h2>
          <p>{program.summary}</p>
        </div>

        <div className="detail-badges">
          <span className={`verification-${program.verification}`}>{verificationLabel(program.verification)}</span>
          <span>{availabilityLabel(program.availability)}</span>
          {program.hq && <span>{program.hq.city}, {program.hq.country}</span>}
        </div>

        <section className="detail-section">
          <h3>What is established</h3>
          <p>{program.evidence}</p>
        </section>

        {program.catalog && (
          <section className="detail-section">
            <h3>Catalog classification</h3>
            <dl className="catalog-facts">
              <div><dt>Platform</dt><dd>{program.catalog.platform}</dd></div>
              <div><dt>Asset class</dt><dd>{program.catalog.assetClass}</dd></div>
              <div><dt>Distribution</dt><dd>{program.catalog.distributionTypes.map((value) => value.replace('Onchain ', '')).join(' + ')}</dd></div>
              <div><dt>Jurisdiction</dt><dd>{program.catalog.jurisdiction || 'Undisclosed'}</dd></div>
            </dl>
            {program.catalog.coveredByName && <p className="catalog-match-note">Mapped to curated program: <strong>{program.catalog.coveredByName}</strong></p>}
          </section>
        )}

        <section className="detail-section">
          <h3>Public ledger identities</h3>
          {!instruments.length && !program.catalog?.identities?.length && (
            <div className="identity-empty">
              <strong>Not publicly verified</strong>
              <p>This announced program is excluded from balance and live-flow matching until a defensible identity is available.</p>
            </div>
          )}
          {instruments.map((instrument, index) => (
            <div className="identity-block" key={instrument.issuanceId || `${instrument.issuer}-${index}`}>
              <div className="identity-label">
                <span>{instrument.kind === 'mpt' ? 'Issuer account' : instrument.kind === 'issuer-series' ? 'Series issuer' : instrument.ticker}</span>
                <a href={accountUrl(instrument.issuer)} target="_blank" rel="noreferrer">Explorer ↗</a>
              </div>
              <div className="copy-row"><code>{instrument.issuer}</code><CopyButton value={instrument.issuer} /></div>
              {instrument.kind === 'classic' && <small>Currency: <code>{instrument.currency}</code></small>}
              {instrument.kind === 'issuer-series' && <small>All non-zero issued currencies are enumerated separately.</small>}
              {instrument.kind === 'mpt' && (
                <>
                  <small>MPT issuance ID</small>
                  <div className="copy-row"><code>{instrument.issuanceId}</code><CopyButton value={instrument.issuanceId} /></div>
                </>
              )}
            </div>
          ))}
          {(program.catalog?.identities || [])
            .filter((identity) => identity.identityType === 'account-only')
            .map((identity) => (
              <div className="identity-block" key={identity.recordId || identity.address}>
                <div className="identity-label">
                  <span>Catalog account reference</span>
                  {identity.issuer && <a href={accountUrl(identity.issuer)} target="_blank" rel="noreferrer">Explorer ↗</a>}
                </div>
                <div className="copy-row"><code>{identity.address}</code><CopyButton value={identity.address} /></div>
                <small>RWA.xyz publishes an account without an exact currency pair. It is not automatically treated as an issuer identity.</small>
              </div>
            ))}
        </section>

        {!!rows.length && (
          <section className="detail-section">
            <h3>Latest observed state</h3>
            <div className="detail-balances">
              {rows.map((row) => (
                <div key={row.key}>
                  <span>{row.ticker}</span>
                  <strong>{formatNumber(row.circulating)} {row.unit}</strong>
                  <small>{row.source === 'xrplmeta' ? 'Third-party indexed supply' : row.excluded > 0 ? `${formatNumber(row.gross)} gross; ${formatNumber(row.excluded)} treasury-held` : `Validated ledger ${Number(row.ledgerIndex || 0).toLocaleString()}`}</small>
                </div>
              ))}
            </div>
          </section>
        )}

        {!!controlSets.length && (
          <section className="detail-section">
            <h3>Issuer controls on ledger</h3>
            <p className="controls-intro">Read from validated account and issuance flags. These describe what the issuer can do on ledger, not what it has promised off ledger.</p>
            {controlSets.map((set) => (
              <div className="control-set" key={set.key}>
                <span className="control-set-label">{set.kind === 'mpt' ? `${set.ticker} issuance` : controlSets.length > 1 ? `${set.ticker} issuer` : 'Issuer account'}</span>
                <div className="control-chips">
                  {set.controls.length
                    ? set.controls.map((control) => <i key={control.key} className={`control-chip tone-${control.tone}`}>{control.label}</i>)
                    : <i className="control-chip tone-open">No issuer restrictions set</i>}
                </div>
              </div>
            ))}
          </section>
        )}

        {!!recentFlows.length && (
          <section className="detail-section">
            <h3>Recent matched activity</h3>
            <div className="detail-flow-list">
              {recentFlows.map((flow) => (
                <a href={flow.href} target="_blank" rel="noreferrer" key={flow.id}>
                  <span><strong>{flow.action}</strong><small>{formatLedgerTime(flow.date)} · {flow.source === 'live' ? 'LIVE' : 'HISTORY'}</small></span>
                  <span>{flow.amount === null || flow.amount === undefined ? flow.transactionType : `${formatNumber(flow.amount, { compact: true })} ${flow.ticker}`} ↗</span>
                </a>
              ))}
            </div>
          </section>
        )}

        <section className="detail-section">
          <h3>Evidence</h3>
          <div className="source-list">
            {(program.sources || []).map((source) => (
              <a key={source.url} href={source.url} target="_blank" rel="noreferrer">{source.label}<span>↗</span></a>
            ))}
          </div>
        </section>
      </aside>
    </div>
  );
}
